import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    MdCloudUpload, MdDelete, MdImage, MdPerson, MdWarning,
    MdCheckCircle, MdKeyboardArrowDown, MdKeyboardArrowLeft,
    MdKeyboardArrowRight, MdCollections, MdStar
} from 'react-icons/md';

import { api, usersAPI } from 'services/api';
import { API_BASE_URL } from 'config';
import { useAuth } from 'context/AuthContext';

// --- SKELETON LOADERS ---
const FormSkeleton = () => (
    <div className="flex flex-col md:flex-row gap-6 animate-pulse">
        <div className="h-64 w-full md:w-1/3 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="h-64 w-full md:w-2/3 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
    </div>
);

const GallerySkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-pulse mt-6">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="h-56 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50" />
        ))}
    </div>
);

// --- ANA BİLEŞEN ---
const AdminGalleryManagement = () => {
    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [images, setImages] = useState([]);
    const [staffList, setStaffList] = useState([]);

    // Form Stateleri
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [selectedStaffId, setSelectedStaffId] = useState('');
    const [description, setDescription] = useState('');

    // Yükleme & Durum Stateleri
    const [loadingData, setLoadingData] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fileInputRef = useRef(null);
    const { logout } = useAuth();

    // Sayfalama (Pagination)
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 8;

    // --- API FETCH ---
    const fetchData = useCallback(async () => {
        setLoadingData(true);
        try {
            const [galleryRes, staffRes] = await Promise.all([
                api.get('/gallery'),
                usersAPI.getStaff()
            ]);

            // HATA ÇÖZÜMÜ: CoreUI'daki gibi galleryRes.images kullanıyoruz!
            if (galleryRes.success || galleryRes.images) {
                const rawData = galleryRes.images || galleryRes.data || [];
                const sortedImages = [...rawData].sort((a, b) => b.id - a.id);
                setImages(sortedImages);
            }

            if (staffRes.success || staffRes.staff) {
                setStaffList(staffRes.staff || []);
            }
        } catch (error) {
            console.error('Galeri veri çekme hatası:', error);
            if (String(error.message).includes('401')) {
                logout();
                return;
            }
            setMessage({ type: 'danger', text: 'Galeri veya personel verisi yüklenirken hata oluştu.' });
        } finally {
            setLoadingData(false);
        }
    }, [logout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- İŞLEMLER ---
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.match('image.*')) {
            setMessage({ type: 'danger', text: 'Lütfen sadece görsel dosyası yükleyin.' });
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // CoreUI'da limit 5MB olarak ayarlanmış
            setMessage({ type: 'warning', text: 'Dosya boyutu çok yüksek (Max 55MB).' });
            return;
        }

        setMessage({ type: '', text: '' });
        setSelectedFile(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setSelectedStaffId('');
        setDescription('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            setMessage({ type: 'danger', text: 'Lütfen bir görsel seçin.' });
            return;
        }

        setUploading(true);
        setMessage({ type: '', text: '' });

        try {
            const formData = new FormData();
            formData.append('image', selectedFile);
            if (selectedStaffId) formData.append('staff_id', selectedStaffId);
            if (description) formData.append('description', description);

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/gallery`, {
                method: 'POST',
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Accept': 'application/json'
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Yükleme sırasında hata oluştu.');
            }

            setMessage({ type: 'success', text: 'Görsel başarıyla yüklendi!' });
            resetForm();
            fetchData();
        } catch (err) {
            setMessage({ type: 'danger', text: err.message || 'Bir hata oluştu.' });
        } finally {
            setUploading(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu fotoğrafı silmek istediğinize emin misiniz?')) return;
        setDeletingId(id);
        setMessage({ type: '', text: '' });

        try {
            const response = await api.delete(`/gallery/${id}`);
            if (response.success) {
                setMessage({ type: 'success', text: 'Görsel başarıyla silindi.' });

                const updatedImages = images.filter(img => img.id !== id);
                setImages(updatedImages);

                const newTotalPages = Math.ceil(updatedImages.length / PAGE_SIZE);
                if (currentPage > newTotalPages && newTotalPages > 0) {
                    setCurrentPage(newTotalPages);
                }
            }
        } catch (err) {
            setMessage({ type: 'danger', text: 'Fotoğraf silinemedi.' });
        } finally {
            setDeletingId(null);
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        }
    };

    // --- SAYFALAMA HESAPLARI ---
    const totalPages = Math.max(1, Math.ceil(images.length / PAGE_SIZE));
    const currentImages = images.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BİLGİ ALANI */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 flex-none">
                        <MdCollections className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Galeri Yönetimi</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Uygulamanızın vitrinini, personellerinize ait çalışma görsellerini buradan yönetin.
                        </p>
                    </div>
                </div>
            </div>

            {/* BİLDİRİMLER */}
            {message.text && (
                <div className={`flex items-center gap-3 p-4 rounded-xl border ${message.type === 'danger' ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400' : 'bg-green-50 border-green-200 text-green-600 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400'}`}>
                    {message.type === 'danger' ? <MdWarning className="text-xl flex-none" /> : <MdCheckCircle className="text-xl flex-none" />}
                    <span className="text-sm font-bold">{message.text}</span>
                </div>
            )}

            {/* 2. YÜKLEME FORMU ALANI */}
            <div className="bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                <h5 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-6">
                    <MdCloudUpload className="text-brand-500" /> Yeni Fotoğraf Yükle
                </h5>

                {loadingData ? <FormSkeleton /> : (
                    <form onSubmit={handleUpload} className="flex flex-col md:flex-row gap-6">

                        {/* SOL: Dosya Seçim & Önizleme Alanı */}
                        <div className="w-full md:w-1/3 flex flex-col">
                            <label
                                className={`relative flex flex-col items-center justify-center h-64 rounded-[20px] border-2 border-dashed cursor-pointer transition-all overflow-hidden ${
                                    previewUrl
                                        ? 'border-brand-500 bg-black/5 dark:bg-black/20'
                                        : 'border-gray-300 hover:border-brand-500 bg-gray-50 hover:bg-gray-100 dark:border-white/20 dark:bg-navy-900 dark:hover:border-brand-400'
                                }`}
                            >
                                {previewUrl ? (
                                    <>
                                        <img src={previewUrl} alt="Önizleme" className="absolute inset-0 w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <span className="text-white font-bold text-sm bg-black/50 px-4 py-2 rounded-xl backdrop-blur-sm">Değiştirmek İçin Tıkla</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center text-gray-400 dark:text-gray-500">
                                        <MdImage className="text-5xl mb-2" />
                                        <span className="text-sm font-bold text-navy-700 dark:text-white">Görsel Seçin</span>
                                        <span className="text-xs mt-1">Maks: 5MB</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept="image/*"
                                    className="hidden"
                                />
                            </label>
                        </div>

                        {/* SAĞ: Form Detayları */}
                        <div className="w-full md:w-2/3 flex flex-col gap-5">
                            <div>
                                <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 flex items-center gap-1">
                                    <MdPerson className="text-brand-500" /> İlgili Personel (Opsiyonel)
                                </label>
                                <div className="relative">
                                    <select
                                        value={selectedStaffId}
                                        onChange={(e) => setSelectedStaffId(e.target.value)}
                                        className="w-full h-12 appearance-none rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all cursor-pointer"
                                    >
                                        <option value="">Genel Galeri (Personel Seçilmezse)</option>
                                        {staffList.map((s, index) => (
                                            <option key={`staff-opt-${s.id}-${index}`} value={s.id}>{s.name} {s.surname}</option>
                                        ))}
                                    </select>
                                    <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xl" />
                                </div>
                            </div>

                            <div className="flex-1">
                                <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Açıklama (Opsiyonel)</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Fotoğraf hakkında kısa bir bilgi..."
                                    className="w-full h-[100px] resize-none rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all"
                                />
                            </div>

                            <div className="flex justify-end pt-2">
                                <button
                                    type="submit"
                                    disabled={uploading || !selectedFile}
                                    className="flex items-center justify-center gap-2 h-12 px-8 rounded-xl bg-brand-500 text-white font-bold text-sm shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-50 transition-all active:scale-95"
                                >
                                    {uploading ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdCloudUpload className="text-xl" /> Yükle ve Kaydet</>}
                                </button>
                            </div>
                        </div>

                    </form>
                )}
            </div>

            {/* 3. GALERİ LİSTESİ */}
            <div className="bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                <h5 className="text-lg font-bold text-navy-700 dark:text-white flex items-center justify-between mb-2">
                    Mevcut Fotoğraflar
                    <span className="text-xs font-bold text-gray-400 bg-gray-100 dark:bg-navy-900 px-3 py-1 rounded-lg">
                        Toplam: {images.length}
                    </span>
                </h5>

                {loadingData ? <GallerySkeleton /> : images.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 opacity-60">
                        <MdImage className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Galeri Boş</h5>
                        <p className="text-sm font-medium text-gray-500 mt-1">Henüz vitrine bir fotoğraf eklenmemiş.</p>
                    </div>
                ) : (
                    <>
                        {/* Galeri Grid Sistemi */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6">
                            {currentImages.map((img, index) => {
                                // HATA ÇÖZÜMÜ: Key hatasını engellemek için index ekledik ve src'de direkt image_url kullanıyoruz (CoreUI ile aynı)
                                return (
                                    <div key={`gallery-img-${img.id}-${index}`} className="group relative flex flex-col bg-white dark:bg-navy-900 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden hover:shadow-md transition-shadow">

                                        <div className="relative h-48 w-full bg-gray-100 dark:bg-navy-800 overflow-hidden">
                                            {/* SİL BUTONU (En Üstte) */}
                                            <div className="absolute top-2 right-2 z-10">
                                                <button
                                                    onClick={() => handleDelete(img.id)}
                                                    disabled={deletingId === img.id}
                                                    className="h-9 w-9 flex items-center justify-center rounded-xl bg-white/90 text-red-500 shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 hover:bg-red-500 hover:text-white"
                                                    title="Sil"
                                                >
                                                    {deletingId === img.id ? <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <MdDelete className="text-lg" />}
                                                </button>
                                            </div>

                                            {/* RESİM (Direkt image_url kullanılıyor) */}
                                            <img
                                                src={img.image_url}
                                                alt={img.description || 'Galeri Resmi'}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://via.placeholder.com/400x300?text=Resim+Bulunamadi';
                                                }}
                                            />

                                            {/* PUANLAMA OVERLAY (CoreUI Birebir) */}
                                            <div className="absolute bottom-0 left-0 w-full p-3 text-white bg-gradient-to-t from-black/80 to-transparent">
                                                <div className="flex items-center gap-1 drop-shadow-md">
                                                    <MdStar className="text-amber-400 text-lg" />
                                                    <span className="font-bold text-sm">{parseFloat(img.average_rating || 0).toFixed(1)}</span>
                                                    <span className="text-[10px] font-medium opacity-80">({img.rating_count || 0} oy)</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detaylar */}
                                        <div className="p-4 flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${img.staff ? 'bg-brand-50 text-brand-500 dark:bg-brand-500/10 dark:text-brand-400' : 'bg-gray-100 text-gray-500 dark:bg-navy-800 dark:text-gray-400'}`}>
                                                    {img.staff ? `${img.staff.name} ${img.staff.surname}` : 'Genel Galeri'}
                                                </span>
                                            </div>
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 line-clamp-2 italic">
                                                {img.description || 'Açıklama eklenmemiş.'}
                                            </p>
                                        </div>

                                    </div>
                                );
                            })}
                        </div>

                        {/* SAYFALAMA */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-white/10">
                                <p className="text-sm font-medium text-gray-500">
                                    <span className="font-bold text-navy-700 dark:text-white">{images.length}</span> fotoğraftan <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, images.length)}</span> gösteriliyor.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowLeft className="text-xl" />
                                    </button>
                                    <div className="flex h-9 min-w-[36px] items-center justify-center rounded-lg bg-brand-500 text-white text-sm font-bold shadow-md px-2">
                                        {currentPage}
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowRight className="text-xl" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default AdminGalleryManagement;